import React, { useCallback, useEffect, useState } from 'react';
import CommonButton from '../../../Components/CommonButton';
import CommonLoader from '../../../Components/CommonLoader';
import CommonTable from '../../../Components/CommonTable';
import CommonActionMenu from '../../../Components/CommonActionMenu';
import NoDataPage from '../../../Components/NoDataPage';
import { useToast } from '../../../Components/CommonToast';
import { useConfirm } from '../../../Components/ConfirmModal';
import { heading3, bodySmall, label as labelPreset, fontWeight, Body } from '../../../Constants/Fonts';
import { risk_portal_appetite, risk_portal_categories, risk_portal_delete_appetite } from '../../../services';
import AppetiteForm from '../Forms/AppetiteForm';

const unwrap = (res) =>
    res?.data?.response_data?.data ?? res?.data?.data ??
    res?.statusCode?.response_data?.data ?? res?.statusCode?.data ?? null;

const asArray = (d, key) => (Array.isArray(d) ? d : (Array.isArray(d?.[key]) ? d[key] : []));

const HEADERS = ['Category', 'Appetite Statement', 'Max Inherent', 'Max Residual', 'Max ALE', 'Actions'];

const ScoreBadge = ({ value, token }) => (
    <Body
        as="span"
        variant={2}
        weight="semibold"
        color="var(--color-text-inverse)"
        style={{
            display: 'inline-block', minWidth: 30, textAlign: 'center',
            padding: '2px 10px', borderRadius: 'var(--radius-sm)',
            background: `var(${token})`,
            fontVariantNumeric: 'tabular-nums',
        }}
    >
        {value ?? '—'}
    </Body>
);
 
export default function RiskAppetite({ canManage = false }) {
    const [appetites, setAppetites] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showForm, setShowForm] = useState(false);

    const { notify, toastNode } = useToast();
    const { confirm, confirmModal } = useConfirm();

    const load = useCallback(async () => {
        try {
            const [appRes, catRes] = await Promise.allSettled([
                risk_portal_appetite(),
                risk_portal_categories(),
            ]);
            setAppetites(appRes.status === 'fulfilled' ? asArray(unwrap(appRes.value), 'appetites') : []);
            setCategories(catRes.status === 'fulfilled' ? asArray(unwrap(catRes.value), 'categories') : []);
        } catch (e) {
            notify('Failed to load risk appetite', 'error');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { setLoading(true); load(); }, [load]);

    const remove = async (a) => {
        const ok = await confirm({
            title: 'Delete appetite threshold',
            message: `Delete the ${a.category__name || 'Global'} tolerance threshold? This can't be undone.`,
            confirmLabel: 'Delete', danger: true, themeColor: false,
        });
        if (!ok) return;
        setBusy(true);
        try {
            await risk_portal_delete_appetite(a.id);
            notify('Appetite threshold deleted', 'success');
            await load();
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Failed to delete threshold', 'error');
        } finally {
            setBusy(false);
        }
    };

    const rows = appetites.map((a) => ({
        data: [
            <Body as="span" weight="semibold" color="var(--color-text-primary)">{a.category__name || 'Global'}</Body>,
            <Body as="span" variant={2} color="var(--color-text-secondary)">{a.appetite_statement || '—'}</Body>,
            <ScoreBadge value={a.max_inherent_score} token="--color-high" />,
            <ScoreBadge value={a.max_residual_score} token="--color-low" />,
            <Body as="span" variant={2} color="var(--color-text-secondary)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {a.max_aggregate_ale ? `$${Number(a.max_aggregate_ale).toLocaleString()}` : '—'}
            </Body>,
            canManage
                ? <CommonActionMenu actions={[{ label: 'Delete', onClick: () => remove(a), danger: true }]} />
                : <Body as="span" variant={2} color="var(--color-text-muted)">—</Body>,
        ],
    }));

    if (loading) return <CommonLoader fullPage label="Loading risk appetite…" />;

    return (
        <div className="rp1_widget">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                <div>
                    <h2 style={heading3({ margin: 0 })}>Risk Appetite &amp; Tolerance</h2>
                    <p style={bodySmall({ color: 'var(--color-text-muted)', margin: '4px 0 0' })}>
                        Per-category ceilings on inherent/residual score (and optional annualised loss). Risks exceeding a threshold are flagged for escalation.
                    </p>
                </div>
                {canManage && <CommonButton text="Add Threshold" onClick={() => setShowForm(true)} />}
            </div>

            <div style={labelPreset({
                color: 'var(--color-text-secondary)', fontWeight: fontWeight.bold,
                textTransform: 'uppercase', letterSpacing: '0.06em', margin: '14px 0 8px',
            })}>
                Tolerance Thresholds
            </div>

            {appetites.length === 0 ? (
                <NoDataPage hint="No appetite thresholds configured yet. Click Add Threshold to create the first." />
            ) : (
                <CommonTable headers={HEADERS} rows={rows} noPadding noScroll />
            )}

            {showForm && (
                <AppetiteForm
                    show
                    categories={categories}
                    onClose={() => setShowForm(false)}
                    onSuccess={(msg) => { notify(msg, 'success'); load(); }}
                    onError={(msg) => notify(msg, 'error')}
                />
            )}

            {busy && <CommonLoader fullPage label="Working…" />}
            {confirmModal}
            {toastNode}
        </div>
    );
}
